import { computed, ref, watch, type Ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'

/** Each responsive tab strip owns its geometry; route links remain the navigation owner. */
export function useDashboardTabMotion(list: Ref<HTMLElement | null>, selected: () => string) {
  const hovered = ref<string | null>(null)
  const style = ref<Record<string, string>>({})
  const measured = ref(false)
  const links = () => Array.from(list.value?.querySelectorAll<HTMLAnchorElement>('[data-dashboard-tab]') ?? [])

  function rectStyle(link: HTMLElement, prefix: string) {
    return {
      [`--${prefix}-x`]: `${link.offsetLeft}px`,
      [`--${prefix}-y`]: `${link.offsetTop}px`,
      [`--${prefix}-scale`]: String(link.offsetWidth / 100),
      [`--${prefix}-height`]: `${link.offsetHeight}px`,
    }
  }

  function measure(animate: boolean) {
    const active = links().find(link => link.dataset.dashboardTab === selected())
    if (!active || !active.offsetWidth) {
      measured.value = false
      return
    }
    style.value = {
      ...style.value,
      ...rectStyle(active, 'tab-active'),
      '--tab-active-duration': animate && measured.value ? '260ms' : '0ms',
    }
    measured.value = true
  }

  function clearHover() {
    hovered.value = null
  }

  function nearest(clientX: number) {
    let winner: HTMLAnchorElement | undefined
    let distance = Infinity
    for (const link of links()) {
      const rect = link.getBoundingClientRect()
      if (!rect.width || link.getAttribute('aria-disabled') === 'true') continue
      const next = Math.abs(clientX - rect.left - rect.width / 2)
      if (next < distance) {
        winner = link
        distance = next
      }
    }
    return winner
  }

  function move(event: PointerEvent) {
    if (event.pointerType !== 'mouse') return
    const link = nearest(event.clientX)
    if (!link) return
    if (hovered.value === link.dataset.dashboardTab) return
    let nearby = false
    if (hovered.value !== null) {
      const highlight = getComputedStyle(list.value!, '::after')
      const transform = new DOMMatrixReadOnly(highlight.transform)
      const width = Number.parseFloat(highlight.width) * Math.abs(transform.a)
      // Use the painted position so rapid passes do not leave the highlight chasing the cursor.
      const distance = Math.abs(link.offsetLeft + link.offsetWidth / 2 - transform.e - width / 2)
      nearby = distance <= Math.max(link.offsetWidth, width) * 1.6
    }
    style.value = {
      ...style.value,
      ...rectStyle(link, 'tab-hover'),
      '--tab-hover-duration': nearby ? '130ms' : '0ms',
    }
    hovered.value = link.dataset.dashboardTab!
  }

  function clickGap(event: MouseEvent) {
    if (!hovered.value || event.detail === 0 || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if ((event.target as Element).closest('a')) return
    nearest(event.clientX)?.click()
  }

  watch([list, selected], () => measure(true), { flush: 'post' })
  useResizeObserver(computed(() => list.value ? [list.value, ...links()] : []), () => {
    clearHover()
    measure(false)
  })

  return { style, hovered, measured, move, clearHover, clickGap }
}
